import { Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUpdateAnonymizerConfig, useAnonymizerConfig } from '@/entities/anonymizer-config';
import type { FioConfig, UpdateFioConfigRequest } from '@/entities/anonymizer-config';
import { ConfigFormLayout } from './ConfigFormLayout';

type FormValues = {
  contextMarkers: string;
  excludedWords: string;
  addNames: string;
  removeNames: string;
  addSurnames: string;
  removeSurnames: string;
  addPatronymics: string;
  removePatronymics: string;
};

const getDefaultValues = (config: FioConfig | undefined): FormValues => ({
  contextMarkers: config?.contextMarkers?.join('\n') ?? '',
  excludedWords: config?.excludedWords?.join('\n') ?? '',
  addNames: '',
  removeNames: '',
  addSurnames: '',
  removeSurnames: '',
  addPatronymics: '',
  removePatronymics: '',
});

type Props = {
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const FioConfigForm: FC<Props> = ({ data, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const config = data as FioConfig | undefined;
  const { mutate: updateConfig, isPending } = useUpdateAnonymizerConfig();
  const { refetch } = useAnonymizerConfig('fio');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(config),
  });

  useEffect(() => {
    if (config) {
      reset(getDefaultValues(config));
    }
  }, [config, reset]);

  useEffect(() => {
    onUnsavedChange?.(isDirty);
  }, [isDirty, onUnsavedChange]);

  const onSubmit = handleSubmit((values) => {
    if (!config) return;
    const body: UpdateFioConfigRequest = {
      dictionaryOverrides: {
        addNames: values.addNames ? values.addNames.split('\n').filter(Boolean) : undefined,
        removeNames: values.removeNames ? values.removeNames.split('\n').filter(Boolean) : undefined,
        addSurnames: values.addSurnames ? values.addSurnames.split('\n').filter(Boolean) : undefined,
        removeSurnames: values.removeSurnames ? values.removeSurnames.split('\n').filter(Boolean) : undefined,
        addPatronymics: values.addPatronymics ? values.addPatronymics.split('\n').filter(Boolean) : undefined,
        removePatronymics: values.removePatronymics ? values.removePatronymics.split('\n').filter(Boolean) : undefined,
      },
      contextMarkers: values.contextMarkers ? values.contextMarkers.split('\n').filter(Boolean) : config.contextMarkers,
      excludedWords: values.excludedWords ? values.excludedWords.split('\n').filter(Boolean) : config.excludedWords,
    };
    updateConfig({ className: 'fio', body });
  });

  const handleReset = () => {
    reset(getDefaultValues(config));
    refetch();
  };

  return (
    <ConfigFormLayout
      title={t('classes.fio')}
      isDirty={isDirty}
      onSave={onSubmit}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6 [&_label]:text-foreground">
        <h3 className="text-lg font-medium">{t('fields.addWords')}</h3>
        <Textarea control={control} name="addNames" label={t('fields.names')} disabled={!canEdit} rows={3} />
        <Textarea control={control} name="addSurnames" label={t('fields.surnames')} disabled={!canEdit} rows={3} />
        <Textarea
          control={control}
          name="addPatronymics"
          label={t('fields.patronymics')}
          disabled={!canEdit}
          rows={3}
        />

        <h3 className="text-lg font-medium">{t('fields.removeWords')}</h3>
        <Textarea control={control} name="removeNames" label={t('fields.names')} disabled={!canEdit} rows={3} />
        <Textarea control={control} name="removeSurnames" label={t('fields.surnames')} disabled={!canEdit} rows={3} />
        <Textarea
          control={control}
          name="removePatronymics"
          label={t('fields.patronymics')}
          disabled={!canEdit}
          rows={3}
        />

        <Textarea
          control={control}
          name="contextMarkers"
          label={t('fields.contextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="excludedWords"
          label={t('fields.excludedWords')}
          disabled={!canEdit}
          rows={5}
        />
      </div>
    </ConfigFormLayout>
  );
};
